import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Activity, 
  ArrowRight, 
  BarChart3, 
  Layers, 
  Grid3X3, 
  TrendingUp,
  Zap,
  Target,
  DollarSign,
  BookOpen,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  PresentationIcon,
  Users,
  Clock,
  Shield,
  Sparkles,
  MessageSquareQuote,
  Calculator,
  ChevronLeft,
  Compass,
  Briefcase,
  Lightbulb,
  Search,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useApp } from '../context/AppContext';
import { getLEIMeasures, getKPIs } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import logger from '../lib/logger';
import { useMessaging } from '../hooks/useMessaging';
import CurrentOpportunityBanner from '../components/impact/CurrentOpportunityBanner';

// Tiny Value Transfer Visual — the small anchor that shows users what
// the modelling tools are actually doing: forecasting movement through
// the value transfer chain. Deliberately compact (chips, not a diagram).
const VALUE_CHAIN_STAGES = ['Challenge', 'Intervention', 'Capability', 'Behavior', 'Business Outcome', 'Financial Impact'];

const TinyValueTransferChain = () => (
  <div className="mt-6 rounded-2xl bg-[#0f1729] ring-1 ring-[#22304d] p-4 md:p-5 shadow-xl" data-testid="tiny-value-transfer-chain">
    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300 mb-3">Value Transfer Chain</p>
    <div className="flex flex-wrap items-center gap-y-2">
      {VALUE_CHAIN_STAGES.map((stage, i) => (
        <React.Fragment key={stage}>
          <span
            className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${
              i === VALUE_CHAIN_STAGES.length - 1
                ? 'bg-teal-400/10 text-teal-300 border-teal-400/30'
                : i === 0
                  ? 'bg-amber-400/10 text-amber-300 border-amber-400/30'
                  : 'bg-white/[0.06] text-slate-200 border-white/10'
            }`}
          >
            {stage}
          </span>
          {i < VALUE_CHAIN_STAGES.length - 1 && (
            <ArrowRight className="h-3.5 w-3.5 text-slate-500 mx-1.5 flex-shrink-0" />
          )}
        </React.Fragment>
      ))}
    </div>
    <p className="text-xs text-slate-400 mt-2.5 italic" data-testid="tiny-chain-caption">
      We model the likely movement through the chain.
    </p>
  </div>
);

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Mission Control brand messaging — sourced from messaging.json (with
  // optional Mongo super-admin overrides) so positioning can evolve
  // without a code deploy. See /admin docs for the messaging layer.
  const heroPrefix = useMessaging('impact_hero.headline_prefix', 'Impact Modelling');
  const heroMain = useMessaging('impact_hero.headline_main', 'Forecast the Value Path Before You Invest');
  const heroDescription = useMessaging('impact_hero.description', 'Explore how interventions influence capability, behavior, business outcomes and financial impact before committing resources.');
  const platformPositioning = useMessaging('platform_positioning.tagline_short', 'Learning Impact Navigation Platform');
  const {
    leiMeasures,
    setLEIMeasures,
    kpis,
    setKPIs,
  } = useApp();
  
  const [isLoading, setIsLoading] = useState(true);
  const [scienceExpanded, setScienceExpanded] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showLEIModal, setShowLEIModal] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showWelcome, setShowWelcome] = useState(() => localStorage.getItem('just_onboarded') === 'true');

  // Carousel slides for ROI analysis journey
  const carouselSlides = [
    {
      step: 1,
      title: 'Learning Interventions',
      description: 'Start with your training programs and learning initiatives',
      image: 'https://images.unsplash.com/photo-1758691736508-a85d1f7d5a77?w=600&h=400&fit=crop',
    },
    {
      step: 2,
      title: 'Learning Effectiveness',
      description: 'Measure impact using the LEI framework (15 measures across 4 pillars)',
      image: 'https://images.unsplash.com/photo-1758691737182-d42aefd6dee8?w=600&h=400&fit=crop',
    },
    {
      step: 3,
      title: 'Workforce Capability',
      description: 'Track skills development and capability improvement',
      image: 'https://images.unsplash.com/photo-1758691737538-220c1902b1ca?w=600&h=400&fit=crop',
    },
    {
      step: 4,
      title: 'Business KPI Impact',
      description: 'Connect learning to productivity, retention, and quality metrics',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
    },
    {
      step: 5,
      title: 'Business ROI',
      description: 'Calculate defensible financial returns and payback period',
      image: 'https://images.unsplash.com/photo-1618044733300-9472054094ee?w=600&h=400&fit=crop',
    },
  ];

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [carouselSlides.length]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
  }, [carouselSlides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + carouselSlides.length) % carouselSlides.length);
  }, [carouselSlides.length]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [leiRes, kpiRes] = await Promise.all([
          getLEIMeasures(),
          getKPIs(),
        ]);
        setLEIMeasures(leiRes.data);
        setKPIs(kpiRes.data);
      } catch (error) {
        logger.error('Error loading data:', error);
        toast.error('Failed to load reference data');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [setLEIMeasures, setKPIs]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const analysisOptions = [
    {
      id: 'single',
      title: 'Single Measure Analysis',
      description: 'Test one LEI measure against multiple KPIs to understand its specific impact on business outcomes.',
      icon: BarChart3,
      color: 'teal',
      path: '/configure',
      features: ['Multi-KPI selection', 'Detailed breakdown', 'LEI Calculator'],
    },
    {
      id: 'portfolio',
      title: 'Portfolio Analysis',
      description: 'Combine multiple LEI measures to see aggregate impact and identify the most effective combination.',
      icon: Layers,
      color: 'navy',
      path: '/portfolio',
      features: ['Multi-measure selection', 'Contribution analysis', 'Portfolio optimization'],
    },
    {
      id: 'matrix',
      title: 'Full Impact Matrix',
      description: 'Generate a complete LEI × KPI relationship matrix showing all possible impact combinations.',
      icon: Grid3X3,
      color: 'purple',
      path: '/matrix',
      features: ['Complete coverage', 'Heat map visualization', 'Executive summary'],
    },
  ];

  const benefits = [
    {
      icon: DollarSign,
      title: 'Quantify Impact',
      description: 'Convert learning improvements into defensible dollar figures',
      color: 'text-emerald-600 bg-emerald-50'
    },
    {
      icon: Shield,
      title: 'Build Credibility',
      description: 'Monte Carlo simulations with 5,000 iterations backing your numbers',
      color: 'text-blue-600 bg-blue-50'
    },
    {
      icon: PresentationIcon,
      title: 'Win Stakeholder Buy-In',
      description: 'Export pitch-ready business cases for executives',
      color: 'text-purple-600 bg-purple-50'
    },
    {
      icon: Clock,
      title: 'Save Hours',
      description: 'Auto-calculate ROI, payback period, and financial impact',
      color: 'text-amber-600 bg-amber-50'
    },
  ];

  const painPoints = [
    "We invested $500K in leadership training... but what did we actually get?",
    "The CFO wants numbers, not completion rates",
    "I know this program works, I just can't prove it"
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Welcome Banner — shown once after onboarding */}
      {showWelcome && (
        <div
          className="relative rounded-2xl border border-teal-200 bg-gradient-to-r from-teal-50 via-white to-emerald-50 p-6 shadow-sm"
          data-testid="welcome-banner"
        >
          <button
            onClick={() => { setShowWelcome(false); localStorage.removeItem('just_onboarded'); }}
            className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors text-lg leading-none"
            aria-label="Dismiss"
            data-testid="welcome-banner-dismiss"
          >&times;</button>

          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center shrink-0">
              <Sparkles className="text-teal-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}! Your organization is ready.
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">Here are a few things you can do next to get the most out of the platform.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: 'Import Data',       desc: 'Upload LEI baselines & KPIs', icon: BookOpen,     path: '/import',     color: 'bg-sky-50 text-sky-600 border-sky-200' },
              { label: 'Create Scenario',    desc: 'Build your first what-if',    icon: Lightbulb,    path: '/scenarios',  color: 'bg-amber-50 text-amber-600 border-amber-200' },
              { label: 'Run an Analysis',    desc: 'Calculate learning ROI',      icon: BarChart3,    path: '/configure',  color: 'bg-purple-50 text-purple-600 border-purple-200' },
              { label: 'View LEI Health',    desc: 'See your health dashboard',   icon: Activity,     path: '/lei-health', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
            ].map(a => (
              <button
                key={a.path}
                onClick={() => navigate(a.path)}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all hover:shadow-md hover:-translate-y-0.5 ${a.color}`}
                data-testid={`welcome-action-${a.path.slice(1)}`}
              >
                <a.icon size={18} />
                <div>
                  <span className="block text-sm font-medium">{a.label}</span>
                  <span className="block text-xs opacity-70">{a.desc}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Hero Section — light analytical theme, matches PageHero
          across the rest of the app. Title uses Seertech cyan
          (#44b6d2) extrabold; body text uses slate-600/700. */}
      <div className="bg-[#0a0f1c] border border-[#1f2a44] rounded-2xl p-6 md:p-8 text-slate-100 shadow-xl">
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Left side - Text content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-4">
              <Activity className="h-8 w-8 text-[#44b6d2]" />
              <span className="text-[#44b6d2] font-semibold text-sm uppercase tracking-wider" data-testid="dashboard-platform-positioning">{platformPositioning}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-extrabold mb-4 leading-tight tracking-tight text-slate-100" data-testid="dashboard-hero-title">
              <span className="text-[#7c8cff]">{heroPrefix}</span> — {heroMain}
            </h1>
            <p className="text-slate-400 text-base leading-relaxed" data-testid="dashboard-hero-description">
              {heroDescription}
            </p>

            {/* What is LEI? Link */}
            <button
              onClick={() => setShowLEIModal(true)}
              className="flex items-center gap-2 mt-4 text-[#44b6d2] hover:text-[#3aa3bd] transition-colors group"
              data-testid="hero-what-is-lei-btn"
            >
              <BookOpen className="h-4 w-4" />
              <span className="text-sm font-medium">New to LEI? Learn how the 5-Pillar model quantifies learning value</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3 mt-6">
              <Button
                size="lg"
                className="bg-[#44b6d2] hover:bg-[#3aa3bd] text-white shadow-sm"
                onClick={() => navigate('/solution-finder')}
                data-testid="hero-solution-finder-btn"
              >
                <Compass className="mr-2 h-5 w-5" />
                Find a Solution
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/5 border border-white/15 hover:bg-white/10 text-slate-100"
                onClick={() => setShowAnalysisModal(true)}
                data-testid="hero-analysis-btn"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Run an Analysis
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/5 border border-white/15 hover:bg-white/10 text-slate-100"
                onClick={() => navigate('/business-case-builder')}
                data-testid="hero-business-case-btn"
              >
                <Briefcase className="mr-2 h-5 w-5" />
                Build Business Case
              </Button>
            </div>

            <TinyValueTransferChain />
          </div>

          {/* Right side - Image Carousel (always right-justified) */}
          <div className="w-full lg:w-96 flex-shrink-0 lg:ml-auto">
            <div className="relative bg-[#0f1729] border border-[#22304d] rounded-xl overflow-hidden shadow-xl">
              {/* Carousel Image */}
              <div className="relative h-48 lg:h-52 overflow-hidden">
                {carouselSlides.map((slide, index) => (
                  <div
                    key={slide.step}
                    className={`absolute inset-0 transition-all duration-500 ease-in-out ${
                      index === currentSlide
                        ? 'opacity-100 translate-x-0'
                        : index < currentSlide
                          ? 'opacity-0 -translate-x-full'
                          : 'opacity-0 translate-x-full'
                    }`}
                  >
                    <img
                      src={slide.image}
                      alt={slide.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  </div>
                ))}

                {/* Step indicator badge */}
                <div className="absolute top-3 left-3 bg-[#44b6d2] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
                  Step {carouselSlides[currentSlide].step} of 5
                </div>

                {/* Navigation arrows */}
                <button
                  onClick={prevSlide}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-slate-50 p-1.5 rounded-full transition-colors shadow-sm"
                  data-testid="hero-carousel-prev"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="h-4 w-4 text-slate-700" />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-slate-50 p-1.5 rounded-full transition-colors shadow-sm"
                  data-testid="hero-carousel-next"
                  aria-label="Next slide"
                >
                  <ChevronRight className="h-4 w-4 text-slate-700" />
                </button>
              </div>

              {/* Slide content */}
              <div className="p-4">
                <h3 className="text-base font-bold text-slate-100 mb-1">
                  {carouselSlides[currentSlide].title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {carouselSlides[currentSlide].description}
                </p>

                {/* Progress dots */}
                <div className="flex items-center justify-center gap-2 mt-4">
                  {carouselSlides.map((_, index) => (
                    <button
                      key={`dot-${index}`}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        index === currentSlide
                          ? 'w-6 bg-[#44b6d2]'
                          : 'w-2 bg-slate-300 hover:bg-slate-400'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>

                {/* Flow indicator */}
                <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
                  <span className="font-medium">Learning</span>
                  <div className="flex-1 flex items-center justify-center gap-1">
                    <ArrowRight className="h-3 w-3" />
                    <span className="hidden sm:inline">LEI Measures</span>
                    <ArrowRight className="h-3 w-3" />
                    <span className="hidden sm:inline">KPIs</span>
                    <ArrowRight className="h-3 w-3" />
                  </div>
                  <span className="font-medium">ROI</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Opportunity — continuity from Mission Control Home.
          Surfaces the live Active Value Constraint as a modelling prompt. */}
      <CurrentOpportunityBanner />

      {/* Analysis Selection Modal */}
      <Dialog open={showAnalysisModal} onOpenChange={setShowAnalysisModal}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading flex items-center gap-2">
              <Zap className="h-6 w-6 text-teal-600" />
              Choose Your Analysis Type
            </DialogTitle>
            <DialogDescription>
              Select the analysis approach that best fits your needs
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {analysisOptions.map((option) => {
              const IconComponent = option.icon;
              const colorClasses = {
                teal: {
                  icon: 'text-teal-600',
                  bg: 'bg-teal-50',
                  border: 'border-teal-200 hover:border-teal-400',
                  badge: 'bg-teal-100 text-teal-700',
                },
                navy: {
                  icon: 'text-navy-700',
                  bg: 'bg-navy-50',
                  border: 'border-navy-200 hover:border-navy-400',
                  badge: 'bg-navy-100 text-navy-700',
                },
                purple: {
                  icon: 'text-purple-600',
                  bg: 'bg-purple-50',
                  border: 'border-purple-200 hover:border-purple-400',
                  badge: 'bg-purple-100 text-purple-700',
                },
              };
              const colors = colorClasses[option.color];
              
              return (
                <div
                  key={option.id}
                  className={`flex items-start gap-4 p-4 rounded-xl border-2 ${colors.border} cursor-pointer transition-all hover:shadow-md group`}
                  onClick={() => {
                    setShowAnalysisModal(false);
                    navigate(option.path);
                  }}
                  data-testid={`modal-analysis-${option.id}`}
                >
                  <div className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <IconComponent className={`h-6 w-6 ${colors.icon}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 group-hover:text-teal-600 transition-colors flex items-center gap-2">
                      {option.title}
                      <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">{option.description}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {option.features.map((feature, idx) => (
                        <span 
                          key={`${feature}-${idx}`} 
                          className={`text-xs px-2 py-0.5 rounded-full ${colors.badge}`}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* LEI Explainer Modal */}
      <Dialog open={showLEIModal} onOpenChange={setShowLEIModal}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading flex items-center gap-2">
              <Activity className="h-6 w-6 text-teal-600" />
              Understanding the Learning Effectiveness Index (LEI)
            </DialogTitle>
            <DialogDescription>
              A strategic, metrics-driven framework that gives L&D professionals the tools to quantify their impact
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Introduction */}
            <div className="bg-gradient-to-r from-navy-50 to-slate-50 rounded-xl p-5 border border-navy-200">
              <p className="text-slate-700 leading-relaxed">
                In an era where business transformation is constant and talent is the ultimate competitive advantage, 
                L&D must evolve beyond delivering programs — it must <span className="font-semibold text-navy-700">drive measurable value</span>. 
                The Learning Effectiveness Index (LEI) brings learning outcomes, operational efficiency, and business 
                relevance into one cohesive model.
              </p>
              <p className="text-slate-600 mt-3 text-sm">
                LEI empowers L&D to operate as a <span className="font-medium">business partner</span>, not just a service provider. 
                It enables alignment with strategic priorities, clarity in resource allocation, and credibility in leadership discussions.
              </p>
            </div>

            {/* Learning Effectiveness Value Chain */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="font-bold text-slate-900 text-base mb-4 text-center">The Learning Effectiveness Value Chain</h3>
              <div className="flex flex-col items-center gap-2">
                <div className="bg-blue-100 text-blue-800 px-6 py-2.5 rounded-lg font-semibold text-sm border border-blue-200 w-48 text-center">
                  Learning Access
                </div>
                <ChevronDown className="h-5 w-5 text-slate-400" />
                <div className="bg-emerald-100 text-emerald-800 px-6 py-2.5 rounded-lg font-semibold text-sm border border-emerald-200 w-48 text-center">
                  Learning Outcomes
                </div>
                <ChevronDown className="h-5 w-5 text-slate-400" />
                <div className="bg-purple-100 text-purple-800 px-6 py-2.5 rounded-lg font-semibold text-sm border border-purple-200 w-48 text-center">
                  Workforce Capability
                </div>
                <ChevronDown className="h-5 w-5 text-slate-400" />
                <div className="bg-amber-100 text-amber-800 px-6 py-2.5 rounded-lg font-semibold text-sm border border-amber-200 w-48 text-center">
                  Business Performance
                </div>
                <ChevronDown className="h-5 w-5 text-slate-400" />
                <div className="bg-teal-100 text-teal-800 px-6 py-2.5 rounded-lg font-semibold text-sm border border-teal-200 w-48 text-center">
                  Financial ROI
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-600 text-center leading-relaxed">
                  Learning only drives business value when improvements in learning effectiveness 
                  translate into workforce capability and operational performance.
                </p>
                <p className="text-sm text-teal-700 font-medium text-center mt-2">
                  The LEI framework measures this entire chain.
                </p>
              </div>
            </div>

            {/* The 5 Pillars */}
            <div>
              <h3 className="font-bold text-slate-900 text-lg mb-4 flex items-center gap-2">
                <Layers className="h-5 w-5 text-teal-600" />
                The 5 Pillars of Learning Effectiveness
              </h3>
              <div className="space-y-3">
                {/* Pillar 1 */}
                <div className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-700 font-bold">1</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Learning Access & Engagement <span className="text-blue-600 text-sm font-normal">(20%)</span></h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Measures breadth and inclusiveness of learning opportunities. Focuses on participation rates, reach, and voluntary engagement.
                      </p>
                      <p className="text-xs text-slate-500 mt-2 italic">
                        Why it matters: You can't improve what people don't experience. High engagement reflects a culture of learning.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pillar 2 */}
                <div className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-emerald-700 font-bold">2</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Learning Performance & Outcomes <span className="text-emerald-600 text-sm font-normal">(30%)</span></h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Evaluates how well programs achieve intended knowledge and behavior outcomes. Includes assessments, skill uplift, NPS scores, and Kirkpatrick Levels 1–3.
                      </p>
                      <p className="text-xs text-slate-500 mt-2 italic">
                        Why it matters: This is the "effectiveness" in Learning Effectiveness — it captures how well learners absorb and apply new knowledge.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pillar 3 */}
                <div className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-700 font-bold">3</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Workforce Capability & Readiness <span className="text-purple-600 text-sm font-normal">(20%)</span></h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Focuses on whether learning is preparing the workforce for current and future roles. Tracks skills gap closure, bench strength, and certification progression.
                      </p>
                      <p className="text-xs text-slate-500 mt-2 italic">
                        Why it matters: This is where learning meets workforce strategy — linking L&D to succession planning and talent mobility.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pillar 4 */}
                <div className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-amber-700 font-bold">4</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Operational Efficiency <span className="text-amber-600 text-sm font-normal">(20%)</span></h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Measures cost, time, and resource effort required to develop, deliver, and manage learning. Supports capacity planning and technology ROI.
                      </p>
                      <p className="text-xs text-slate-500 mt-2 italic">
                        Why it matters: Learning must prove not only impact, but efficiency. This demonstrates stewardship over resources and scalability.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pillar 5 */}
                <div className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-teal-700 font-bold">5</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Business Impact & Value Realization <span className="text-teal-600 text-sm font-normal">(10%)</span></h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Connects learning to downstream business outcomes — productivity, performance, retention, and cost-effectiveness.
                      </p>
                      <p className="text-xs text-slate-500 mt-2 italic">
                        Why it matters: The "holy grail" for L&D — proving learning investments drive real business results and unlocking strategic alignment.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Value Proposition */}
            <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
              <h3 className="font-bold text-teal-900 mb-3 flex items-center gap-2">
                <Target className="h-5 w-5" />
                Why LEI Matters for Learning Leaders
              </h3>
              <ul className="space-y-2 text-sm text-teal-800">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Execution Realism:</strong> Know exactly how much of your business plan your workforce can deliver</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Early Warning:</strong> See where capability and motivation are decaying before performance drops</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span><strong>ROI Proof:</strong> Every point increase in LEI corresponds to measurable improvement in business outcomes</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Strategic Credibility:</strong> Move from cost center to value generator with defensible data</span>
                </li>
              </ul>
            </div>

            {/* CTA */}
            <div className="flex flex-wrap gap-3 pt-2">
              <Button 
                onClick={() => {
                  setShowLEIModal(false);
                  navigate('/lei-reference');
                }}
                className="bg-navy-700 hover:bg-navy-800"
              >
                <BookOpen className="mr-2 h-4 w-4" />
                View All LEI Measures
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setShowLEIModal(false);
                  setShowAnalysisModal(true);
                }}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Start Your Analysis
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Benefits Strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {benefits.map((benefit, idx) => (
          <div key={benefit.title} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:shadow-md transition-shadow">
            <div className={`p-2.5 rounded-lg ${benefit.color}`}>
              <benefit.icon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 text-sm">{benefit.title}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{benefit.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Two Ways to Get Started Section */}
      <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 p-6 md:p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">Two Ways to Get Started</h2>
          <p className="text-slate-600">Choose your path based on where you're starting from</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Path 1: Solution Finder - Top Down */}
          <div 
            className="relative bg-white rounded-xl border-2 border-teal-200 hover:border-teal-400 p-6 cursor-pointer group transition-all hover:shadow-lg"
            onClick={() => navigate('/solution-finder')}
            data-testid="path-solution-finder"
          >
            {/* Recommended Badge */}
            <div className="absolute -top-3 left-4 bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              RECOMMENDED
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Compass className="h-7 w-7 text-teal-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900 group-hover:text-teal-600 transition-colors flex items-center gap-2">
                  "I have a Business Problem"
                  <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Start with the outcome you want to improve. We'll identify the interventions most likely to improve value transfer.
                </p>
              </div>
            </div>
            
            {/* Flow Visualization */}
            <div className="mt-5 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5 text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded-lg">
                  <Target className="h-3.5 w-3.5" />
                  <span className="font-medium">KPI Issue</span>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300" />
                <div className="flex items-center gap-1.5 text-purple-700 bg-purple-50 px-2.5 py-1.5 rounded-lg">
                  <Lightbulb className="h-3.5 w-3.5" />
                  <span className="font-medium">Intervention</span>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300" />
                <div className="flex items-center gap-1.5 text-teal-700 bg-teal-50 px-2.5 py-1.5 rounded-lg">
                  <DollarSign className="h-3.5 w-3.5" />
                  <span className="font-medium">ROI</span>
                </div>
              </div>
            </div>
            
            <Button className="w-full mt-4 bg-teal-600 hover:bg-teal-700 text-white">
              <Compass className="mr-2 h-4 w-4" />
              Open Solution Finder
            </Button>
          </div>
          
          {/* Path 2: Analysis Tools - Bottom Up */}
          <div 
            className="relative bg-white rounded-xl border-2 border-navy-200 hover:border-navy-400 p-6 cursor-pointer group transition-all hover:shadow-lg"
            onClick={() => setShowAnalysisModal(true)}
            data-testid="path-analysis"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-navy-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <BarChart3 className="h-7 w-7 text-navy-700" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900 group-hover:text-navy-700 transition-colors flex items-center gap-2">
                  "I know my Learning Measures"
                  <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Start with capability, behavior or learning metrics. We'll forecast downstream business impact.
                </p>
              </div>
            </div>
            
            {/* Flow Visualization */}
            <div className="mt-5 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5 text-blue-700 bg-blue-50 px-2.5 py-1.5 rounded-lg">
                  <Activity className="h-3.5 w-3.5" />
                  <span className="font-medium">LEI Measures</span>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300" />
                <div className="flex items-center gap-1.5 text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded-lg">
                  <Target className="h-3.5 w-3.5" />
                  <span className="font-medium">KPI Impact</span>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300" />
                <div className="flex items-center gap-1.5 text-teal-700 bg-teal-50 px-2.5 py-1.5 rounded-lg">
                  <DollarSign className="h-3.5 w-3.5" />
                  <span className="font-medium">ROI</span>
                </div>
              </div>
            </div>
            
            <Button className="w-full mt-4 bg-navy-700 hover:bg-navy-800 text-white">
              <Sparkles className="mr-2 h-4 w-4" />
              Choose Analysis Type
            </Button>
          </div>
        </div>
        
        {/* Converging Arrow to Business Case */}
        <div className="mt-8 pt-6 border-t border-slate-200">
          <div className="flex flex-col items-center">
            <p className="text-sm text-slate-500 mb-3">Both paths lead to...</p>
            <div 
              className="flex items-center gap-3 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl px-6 py-4 cursor-pointer hover:shadow-md transition-all group"
              onClick={() => navigate('/business-case-builder')}
              data-testid="path-business-case"
            >
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <Briefcase className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 group-hover:text-purple-600 transition-colors">
                  Export as Business Case
                </h4>
                <p className="text-xs text-slate-500">PDF, Word, or PowerPoint with AI-powered narratives</p>
              </div>
              <ArrowRight className="h-5 w-5 text-purple-400 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </div>

      {/* Pain Points Section */}
      <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquareQuote className="h-5 w-5 text-slate-400" />
            <h2 className="font-bold text-slate-700">Sound Familiar?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {painPoints.map((point, idx) => (
              <div key={`pain-${idx}-${point.slice(0,15)}`} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-slate-200">
                <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-600 italic">"{point}"</p>
              </div>
            ))}
          </div>
          <div className="mt-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-teal-800">
                <span className="font-semibold">This tool solves these challenges</span> by connecting your learning metrics 
                to business KPIs using research-backed models, giving you credible financial projections you can defend.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Choose Your Analysis Section */}
      <div>
        <div className="flex items-center gap-2 mb-6">
          <Zap className="h-6 w-6 text-teal-600" />
          <h2 className="text-2xl font-heading font-bold text-slate-900">Choose Your Analysis</h2>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {analysisOptions.map((option) => {
            const IconComponent = option.icon;
            const colorClasses = {
              teal: {
                icon: 'text-teal-600',
                bg: 'bg-teal-50',
                border: 'border-teal-200',
                button: 'bg-teal-600 hover:bg-teal-700',
                badge: 'bg-teal-100 text-teal-700',
              },
              navy: {
                icon: 'text-navy-700',
                bg: 'bg-navy-50',
                border: 'border-navy-200',
                button: 'bg-navy-700 hover:bg-navy-800',
                badge: 'bg-navy-100 text-navy-700',
              },
              purple: {
                icon: 'text-purple-600',
                bg: 'bg-purple-50',
                border: 'border-purple-200',
                button: 'bg-purple-600 hover:bg-purple-700',
                badge: 'bg-purple-100 text-purple-700',
              },
            };
            const colors = colorClasses[option.color];
            
            return (
              <Card 
                key={option.id} 
                className={`${colors.border} hover:shadow-lg transition-shadow cursor-pointer group`}
                onClick={() => navigate(option.path)}
                data-testid={`analysis-card-${option.id}`}
              >
                <CardHeader>
                  <div className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center mb-4`}>
                    <IconComponent className={`h-6 w-6 ${colors.icon}`} />
                  </div>
                  <CardTitle className="text-lg font-heading group-hover:text-teal-600 transition-colors">
                    {option.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-slate-600">
                    {option.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {option.features.map((feature, idx) => (
                      <span 
                        key={`${feature}-${idx}`} 
                        className={`text-xs px-2 py-1 rounded-full ${colors.badge}`}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                  <Button 
                    className={`w-full ${colors.button} text-white`}
                    data-testid={`start-${option.id}-btn`}
                  >
                    Start Analysis
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* How It Works Section */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-navy-700" />
            <CardTitle className="text-lg font-heading">How It Works</CardTitle>
          </div>
          <CardDescription>
            How a modelling run moves from constraint to confident decision
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Methodology Flow */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-teal-700 font-bold">1</span>
              </div>
              <p className="font-medium text-slate-900 text-sm">Define The Constraint</p>
              <p className="text-xs text-slate-500 mt-1">Pinpoint where value transfer is breaking down</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-teal-700 font-bold">2</span>
              </div>
              <p className="font-medium text-slate-900 text-sm">Model The Intervention</p>
              <p className="text-xs text-slate-500 mt-1">Evidence-based factors link the intervention to business KPIs</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-teal-700 font-bold">3</span>
              </div>
              <p className="font-medium text-slate-900 text-sm">Simulate Value Transfer</p>
              <p className="text-xs text-slate-500 mt-1">5,000 scenarios model movement through the chain</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-teal-700 font-bold">4</span>
              </div>
              <p className="font-medium text-slate-900 text-sm">Compare Outcomes</p>
              <p className="text-xs text-slate-500 mt-1">Rank scenarios by projected ROI and expected value recovery</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* The Science Behind It - Collapsible */}
      <Card className="border-slate-200">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors rounded-t-lg"
          onClick={() => setScienceExpanded(!scienceExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-navy-700" />
              <CardTitle className="text-lg font-heading font-bold">Why These Forecasts Are Credible</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">
                {scienceExpanded ? 'Click to collapse' : 'Our methodology is grounded in peer-reviewed research'}
              </span>
              {scienceExpanded ? (
                <ChevronDown className="h-5 w-5 text-slate-400" />
              ) : (
                <ChevronRight className="h-5 w-5 text-slate-400" />
              )}
            </div>
          </div>
        </CardHeader>
        
        {scienceExpanded && (
          <CardContent className="pt-0">
            <div className="bg-navy-50 rounded-lg p-6 border border-navy-200">
              {/* Why This Matters */}
              <div className="mb-6 p-4 bg-white border border-slate-200 rounded-lg border border-navy-100">
                <p className="text-sm text-slate-700 leading-relaxed">
                  Traditional L&D metrics like completion rates don't tell the full story. By connecting 
                  <span className="font-medium text-navy-700"> Learning Effectiveness Index (LEI) </span> 
                  measures to business KPIs, you can demonstrate the actual value of training investments 
                  in terms executives understand: <span className="font-medium text-teal-600">dollars, productivity, and ROI</span>.
                </p>
              </div>

              {/* ROI Waterfall Framework */}
              <div className="mb-6 bg-white rounded-xl p-5 border border-slate-200">
                <h3 className="font-bold text-slate-900 text-base mb-3 text-center">The ROI Waterfall Framework</h3>
                <p className="text-sm text-slate-600 text-center mb-4">
                  Our model simulates the complete causal chain from learning improvement to financial returns:
                </p>
                
                {/* Causal Chain Visual */}
                <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-0">
                  {/* Learning Improvement */}
                  <div className="flex flex-col items-center">
                    <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-semibold text-sm border border-blue-200 text-center min-w-[140px]">
                      Learning Improvement
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-400 hidden md:block" />
                  <ChevronDown className="h-5 w-5 text-slate-400 md:hidden" />
                  
                  {/* Capability Impact */}
                  <div className="flex flex-col items-center">
                    <div className="bg-purple-100 text-purple-800 px-4 py-2 rounded-lg font-semibold text-sm border border-purple-200 text-center min-w-[140px]">
                      Capability Impact
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-400 hidden md:block" />
                  <ChevronDown className="h-5 w-5 text-slate-400 md:hidden" />
                  
                  {/* KPI Impact */}
                  <div className="flex flex-col items-center">
                    <div className="bg-amber-100 text-amber-800 px-4 py-2 rounded-lg font-semibold text-sm border border-amber-200 text-center min-w-[140px]">
                      KPI Impact
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-400 hidden md:block" />
                  <ChevronDown className="h-5 w-5 text-slate-400 md:hidden" />
                  
                  {/* Financial Value */}
                  <div className="flex flex-col items-center">
                    <div className="bg-emerald-100 text-emerald-800 px-4 py-2 rounded-lg font-semibold text-sm border border-emerald-200 text-center min-w-[140px]">
                      Financial Value
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-400 hidden md:block" />
                  <ChevronDown className="h-5 w-5 text-slate-400 md:hidden" />
                  
                  {/* Net ROI */}
                  <div className="flex flex-col items-center">
                    <div className="bg-teal-500 text-white px-4 py-2 rounded-lg font-bold text-sm border border-teal-600 text-center min-w-[140px]">
                      Net ROI
                    </div>
                  </div>
                </div>

                {/* Caption */}
                <div className="mt-5 pt-4 border-t border-slate-200 text-center">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Input your improvement assumptions and receive estimated business and financial outcomes.
                  </p>
                  <p className="text-sm text-teal-700 font-medium mt-2">
                    The waterfall shows the complete value creation path from learning to ROI.
                  </p>
                </div>
              </div>

              {/* How the Math Works - Section Header */}
              <div className="mb-4">
                <h3 className="font-bold text-slate-800 text-base flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-navy-600" />
                  How the Calculations Work
                </h3>
                <p className="text-xs text-slate-500 mt-1">The formulas behind each step of the waterfall</p>
              </div>

              {/* Three-Part Formula Explanation */}
              <div className="space-y-4">
                {/* Part 1: Learning Improvement */}
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-700 font-bold text-xs">1</span>
                    </div>
                    <p className="font-medium text-slate-800 text-sm">Measuring Learning Improvement</p>
                  </div>
                  <p className="font-mono text-blue-700 text-sm bg-blue-50 px-3 py-2 rounded mb-3">
                    Learning Improvement = LEI Measure × % Improvement × Volume Factor
                  </p>
                  <div className="text-xs text-slate-600 space-y-2">
                    <p>
                      <span className="font-medium text-slate-700">LEI Measures</span> are {leiMeasures.length} research-backed 
                      indicators of learning effectiveness — from training scope and NPS to skills coverage and cost efficiency.
                    </p>
                    <p>
                      <span className="font-medium text-slate-700">% Improvement</span> can be entered directly 
                      (<span className="text-blue-600">Simple mode</span>) or calculated from your actual data 
                      (<span className="text-blue-600">Advanced mode</span> with our LEI Calculator).
                    </p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="mt-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-0 h-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/lei-reference');
                    }}
                  >
                    <BookOpen className="mr-1 h-3 w-3" />
                    <span className="text-xs">View all {leiMeasures.length} LEI Measures →</span>
                  </Button>
                </div>

                {/* Part 2: Capability Impact - NEW */}
                <div className="bg-white rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-700 font-bold text-xs">2</span>
                    </div>
                    <p className="font-medium text-slate-800 text-sm">Calculating Capability Impact</p>
                  </div>
                  <p className="font-mono text-purple-700 text-sm bg-purple-50 px-3 py-2 rounded mb-3">
                    Capability Impact = Learning Improvement × Capability Transfer Rate × Dampening
                  </p>
                  <div className="text-xs text-slate-600 space-y-2">
                    <p>
                      <span className="font-medium text-slate-700">Capability Transfer Rate</span> models how much 
                      learning actually converts to workforce capability. Research suggests well-designed learning 
                      achieves <span className="text-purple-600 font-medium">70-85% transfer</span> to on-the-job capability.
                    </p>
                    <p>
                      <span className="font-medium text-slate-700">Dampening Factors</span> account for real-world 
                      execution challenges: adoption speed, leadership alignment, and operational readiness.
                    </p>
                  </div>
                  <div className="mt-3 p-2 bg-purple-50 rounded border border-purple-100">
                    <p className="text-xs text-purple-700">
                      <strong>Why this matters:</strong> Not all learning translates to capability. This step ensures 
                      ROI projections reflect the realistic transfer of knowledge to workplace performance.
                    </p>
                  </div>
                </div>

                {/* Part 3: KPI Impact */}
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center">
                      <span className="text-amber-700 font-bold text-xs">3</span>
                    </div>
                    <p className="font-medium text-slate-800 text-sm">Calculating KPI Impact</p>
                  </div>
                  <p className="font-mono text-amber-700 text-sm bg-amber-50 px-3 py-2 rounded mb-3">
                    KPI Impact = Capability Impact × Weight × Elasticity
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                    <div className="bg-slate-50 p-2 rounded">
                      <p className="text-amber-600 font-medium">Weight</p>
                      <p className="text-slate-500">How strongly this capability affects the KPI</p>
                    </div>
                    <div className="bg-slate-50 p-2 rounded">
                      <p className="text-amber-600 font-medium">Elasticity</p>
                      <p className="text-slate-500">How responsive the KPI is to capability changes</p>
                    </div>
                    <div className="bg-slate-50 p-2 rounded">
                      <p className="text-amber-600 font-medium">Adjustments</p>
                      <p className="text-slate-500">Scale, industry, modality & time factors</p>
                    </div>
                  </div>
                </div>

                {/* Part 4: Financial ROI */}
                <div className="bg-white rounded-lg p-4 border border-teal-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
                      <span className="text-teal-700 font-bold text-xs">4</span>
                    </div>
                    <p className="font-medium text-slate-800 text-sm">Calculating Financial ROI</p>
                  </div>
                  <p className="font-mono text-teal-700 text-sm bg-teal-50 px-3 py-2 rounded mb-3">
                    Net ROI = (Financial Value - Investment) / Investment
                  </p>
                  <div className="text-xs text-slate-600">
                    <p>
                      <span className="font-medium text-slate-700">Financial Value</span> is derived by applying 
                      KPI improvements to baseline financial metrics (e.g., revenue per employee, cost savings).
                    </p>
                  </div>
                </div>
              </div>

              {/* Bottom Line */}
              <div className="mt-4 flex items-center gap-2 text-xs text-slate-500 bg-white border border-slate-200 p-3 rounded-lg">
                <DollarSign className="h-4 w-4 text-teal-600 flex-shrink-0" />
                <p>
                  The result? A defensible financial impact figure you can present to stakeholders — backed by 
                  Monte Carlo simulation with 5,000 iterations for confidence level.
                </p>
              </div>
              
              {/* Reference Links */}
              <div className="mt-6 flex flex-wrap gap-3">
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/lei-reference')}
                  className="bg-white hover:bg-slate-50 border-slate-300"
                >
                  <BookOpen className="h-4 w-4 mr-2 text-teal-600" />
                  LEI Measures Guide
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/research')}
                  className="bg-white hover:bg-slate-50 border-slate-300"
                >
                  <Award className="h-4 w-4 mr-2 text-amber-600" />
                  Research & Evidence
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Credibility Stats Bar */}
      <div className="bg-slate-900 rounded-xl p-6 text-white">
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
          <div className="text-center">
            <p className="text-3xl font-bold text-teal-400">{leiMeasures.length}</p>
            <p className="text-xs text-slate-400 mt-1">Research-Backed LEI Measures</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-teal-400">{kpis.length}</p>
            <p className="text-xs text-slate-400 mt-1">Business KPIs</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-teal-400">5,000</p>
            <p className="text-xs text-slate-400 mt-1">Simulation Iterations</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-teal-400">90%</p>
            <p className="text-xs text-slate-400 mt-1">Confidence Level</p>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="flex flex-wrap justify-center gap-3">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/lei-reference')}
          data-testid="view-lei-measures-btn"
        >
          <TrendingUp className="mr-2 h-4 w-4" />
          View LEI Measures
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/scenarios')}
          data-testid="view-saved-scenarios-btn"
        >
          <Target className="mr-2 h-4 w-4" />
          Saved Scenarios
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/import')}
          data-testid="import-data-btn"
        >
          <Users className="mr-2 h-4 w-4" />
          Import Data
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/prospectus')}
          data-testid="business-case-btn"
        >
          <PresentationIcon className="mr-2 h-4 w-4" />
          Business Case Builder
        </Button>
      </div>
    </div>
  );
};

export default Dashboard;
